'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroPanel from './components/HeroPanel';
import VisaPanel from './components/VisaPanel';
import CostPanel from './components/CostPanel';
import CreditPanel from './components/CreditPanel';
import StudentVoicePanel from './components/StudentVoicePanel';
import TimelinePanel from './components/TimelinePanel';
import CTAPanel from './components/CTAPanel';

const TOTAL_PANELS = 7;

export default function HomePage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = useState(0);

  const scrollToPanel = useCallback((index: number) => {
    if (!scrollRef.current) return;
    const panelWidth = window.innerWidth;
    scrollRef.current.scrollTo({
      left: panelWidth * index,
      behavior: 'smooth',
    });
  }, []);

  // Track active panel on scroll
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const panelWidth = window.innerWidth;
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / panelWidth);
      setActivePanel(Math.max(0, Math.min(TOTAL_PANELS - 1, index)));
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        scrollToPanel(Math.min(activePanel + 1, TOTAL_PANELS - 1));
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        scrollToPanel(Math.max(activePanel - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activePanel, scrollToPanel]);

  // Convert vertical wheel to horizontal scroll
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // On desktop, redirect vertical scroll to horizontal
      if (window.innerWidth > 768) {
        e.preventDefault();
        const delta = e.deltaY || e.deltaX;
        // Snap to next/prev panel based on direction
        if (delta > 30) {
          scrollToPanel(Math.min(activePanel + 1, TOTAL_PANELS - 1));
        } else if (delta < -30) {
          scrollToPanel(Math.max(activePanel - 1, 0));
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [activePanel, scrollToPanel]);

  return (
    <>
      <Header
        activePanel={activePanel}
        totalPanels={TOTAL_PANELS}
        onDotClick={scrollToPanel}
      />

      {/* Horizontal scroll container */}
      <div ref={scrollRef} className="h-scroll-container">
        <HeroPanel />
        <VisaPanel />
        <CostPanel />
        <CreditPanel />
        <StudentVoicePanel />
        <TimelinePanel />
        <CTAPanel />
      </div>

      <Footer />

      {/* Left/Right navigation arrows — desktop only */}
      <div className="hidden md:block">
        {activePanel > 0 && (
          <button
            onClick={() => scrollToPanel(activePanel - 1)}
            className="fixed left-6 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-10 h-10 transition-all duration-200"
            style={{
              background: 'rgba(245,240,232,0.9)',
              border: '1px solid rgba(184,175,166,0.5)',
              backdropFilter: 'blur(4px)',
            }}
            aria-label="Previous panel"
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--washi)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(245,240,232,0.9)')}
          >
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M13 5H1M6 1L1 5L6 9" stroke="var(--sumi)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {activePanel < TOTAL_PANELS - 1 && (
          <button
            onClick={() => scrollToPanel(activePanel + 1)}
            className="fixed right-6 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-10 h-10 transition-all duration-200"
            style={{
              background: activePanel === TOTAL_PANELS - 2
                ? 'var(--vermillion)'
                : 'rgba(245,240,232,0.9)',
              border: `1px solid ${activePanel === TOTAL_PANELS - 2 ? 'var(--vermillion)' : 'rgba(184,175,166,0.5)'}`,
              backdropFilter: 'blur(4px)',
            }}
            aria-label="Next panel"
            onMouseEnter={e => {
              if (activePanel !== TOTAL_PANELS - 2) {
                (e.currentTarget as HTMLElement).style.background = 'var(--washi)';
              }
            }}
            onMouseLeave={e => {
              if (activePanel !== TOTAL_PANELS - 2) {
                (e.currentTarget as HTMLElement).style.background = 'rgba(245,240,232,0.9)';
              }
            }}
          >
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path
                d="M1 5H13M8 1L13 5L8 9"
                stroke={activePanel === TOTAL_PANELS - 2 ? 'var(--washi)' : 'var(--sumi)'}
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Panel counter — bottom right */}
      <div
        className="hidden md:flex fixed bottom-12 right-8 z-50 items-center gap-3"
        style={{ pointerEvents: 'none' }}
      >
        <span className="font-plex-mono" style={{ fontSize: '10px', letterSpacing: '0.15em', color: 'var(--tatami)', textTransform: 'uppercase' }}>
          {String(activePanel + 1).padStart(2, '0')} / {String(TOTAL_PANELS).padStart(2, '0')}
        </span>
      </div>
    </>
  );
}